import React, { useEffect, useState, useCallback } from 'react';
import { createStyles } from 'utils/createStyle';
import { Btn } from 'components/btn/Btn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCopy,
    faCheck,
    faEnvelope,
    faSignOut,
    faCommentAltLines,
} from '@fortawesome/pro-regular-svg-icons';
import { colors } from 'styles/settings';
import { createEnrollmentCodeFromOrg } from 'common/api/crm/family/service';
import Stars from 'media/images/stars.svg';
import LogoIconLightBg from 'media/images/LogoIconLightBg.svg';
import FlipGiveIcon from 'media/images/flipgive/FlipGiveIcon.svg';
import { Branding, IS_FLIPGIVE, WEB_ENROLLMENT_ORIGIN } from 'common/env';
import { useRootSelector } from 'rootStore';
import { selectDefaultOrganization, selectHomeDetails } from 'common/features/store/duck/home/duck';
import Skeleton from 'react-loading-skeleton';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import ResponsiveContainer, {
    ContainerType,
} from 'components/responsive-containers/ResponsiveContainer';
import { useP2PInviteSettings } from '../../coordinator-dashboard/p2p-invite-settings/P2PInviteSettings';
import { logP2PInviteFamily } from 'config/analytics';
import useQueryParams from 'hooks/useQueryParams';
import { useSelector } from 'react-redux';
import {
    selectIsInviteFriendsFamilyDrawingWebEnabled,
    selectIsAppsFlyerP2PWebEnabled,
    selectIsP2PInviteCopyConfigWebEnabled,
} from 'common/features/store/duck/ffs';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useIsMobileViewport } from 'hooks/useIsMobileViewport';
import { getP2PEnrollmentUrl, getP2PInviteRedirectPath } from 'utils/getEnrollmentUrl';
import { RemoteConfigWeb } from 'config/RemoteConfigWeb';
import { getP2PCopyWeb } from './p2pCopy';

const styles = createStyles({
    header: 'text-brand-dark font-semibold text-2xl mb-10 text-center',
    pageContainer: [
        {
            margin: '0 auto',
            maxWidth: 480,
            '@media (max-width: 640px)': { maxWidth: 'auto' },
        },
    ],
    pageContainerDrawing: [
        {
            margin: '0 auto',
            maxWidth: 500,
            '@media (max-width: 640px)': { maxWidth: 'auto', marginLeft: -10, marginRight: -10 },
        },
    ],
    contentContainer: [
        'flex flex-col pt-4',
        {
            maxWidth: 480,
            '@media (max-width: 640px)': { maxWidth: 'auto' },
        },
    ],
    contentContainerDrawing: [
        'flex flex-col pt-4',
        {
            maxWidth: 500,
            '@media (max-width: 640px)': { maxWidth: 'auto', marginLeft: -10, marginRight: -10 },
        },
    ],
    shareYourLink: 'text-grey-darkest font-semibold',
    copyButton: [
        'flex font-semibold text-sm text-brand text-left transition-shadow align-end',
        {
            '&:disabled': {
                color: colors.success,
                borderColor: colors.white,
                pointerEvents: 'none',
            },
            '&:active': {
                transition: 'none',
                boxShadow: 'none',
            },
            '&:focus': {
                transition: 'none',
                boxShadow: 'none',
                outline: 'none',
            },
        },
    ],
    shareMobileIcon: [
        'self-end translate-x',
        {
            transform: 'rotate(-90deg)',
        },
    ],

    subHeader: 'text-brand-dark font-semibold text-xl mb-1 text-center px-3 sm:px-4',
    btn: ['w-full sm:w-auto mt-3 mb-5'],
});

export enum P2pSources {
    Settings = 'settings',
    Header = 'header',
    AccountMenu = 'account_menu',
}

const parseP2pInviteSource = (sourceParam: string | null) => {
    let source = P2pSources.Settings;

    // Validate that we're getting the right one, otherwise we'll just default to `settings`
    if (sourceParam && (sourceParam == P2pSources.Header || sourceParam == P2pSources.Settings)) {
        source = sourceParam;
    }

    return source;
};

const InviteFriendsFamily = () => {
    const params = useQueryParams();
    const source = parseP2pInviteSource(params.get('source'));
    const defaultOrganization: any = useRootSelector(selectDefaultOrganization);
    const selectedOrganizationId = defaultOrganization?.id as string;
    const [isCopied, copyText] = useCopyToClipboard(5000);
    const homeDetails = useRootSelector(selectHomeDetails);
    const [copyInviteUrl, setCopyInviteUrl] = useState('');
    const [textInviteUrl, setTextInviteUrl] = useState('');
    const [emailInviteUrl, setEmailInviteUrl] = useState('');
    const [shareInviteUrl, setShareInviteUrl] = useState('');
    const [enrollmentCode, setEnrollmentCode] = useState('');
    const { isP2PInviteEnabled } = useP2PInviteSettings();
    const isInviteFriendsFamilyDrawingEnabled = useSelector(
        selectIsInviteFriendsFamilyDrawingWebEnabled
    );
    const isAppsFlyerP2PEnabled = useSelector(selectIsAppsFlyerP2PWebEnabled);
    const isP2PInviteCopyConfigWebEnabled = useSelector(selectIsP2PInviteCopyConfigWebEnabled);
    const isMobileWeb = useIsMobileViewport('(max-width: 639px)');
    const inviteSource = isMobileWeb ? 'mweb_invite' : 'desktop_invite';
    const [, setRemoteConfigReady] = useState(false);

    // Ensure Firebase Remote Config is loaded so p2p_invite_copy_web is available when flag is on.
    // setRemoteConfigReady(true) after refresh forces a re-render so copyForShare uses fetched copy.
    useEffect(() => {
        if (!isP2PInviteEnabled || !isP2PInviteCopyConfigWebEnabled) {
            return;
        }
        RemoteConfigWeb.initialize()
            .then(() => RemoteConfigWeb.refresh())
            .then(() => {
                setRemoteConfigReady(true);
            });
    }, [isP2PInviteEnabled, isP2PInviteCopyConfigWebEnabled]);

    const orgName = defaultOrganization?.name ?? '';
    const referrerName = homeDetails?.profile?.firstName ?? '';

    // Share drawer uses SMS copy (same as text invite), with a share-specific URL.
    const copyForShare = getP2PCopyWeb(
        orgName,
        referrerName,
        shareInviteUrl,
        isP2PInviteCopyConfigWebEnabled
    );
    const shareData = {
        text: copyForShare.smsBody,
        url: shareInviteUrl,
    };

    useScrollToTop();

    useEffect(() => {
        (async () => {
            if (selectedOrganizationId) {
                const result = await createEnrollmentCodeFromOrg(selectedOrganizationId);
                if (!result.error) {
                    if (result.data.code) {
                        setEnrollmentCode(result.data.code);
                        setCopyInviteUrl(
                            getP2PEnrollmentUrl(
                                result.data.code,
                                'copy',
                                inviteSource,
                                isAppsFlyerP2PEnabled
                            )
                        );
                        setTextInviteUrl(
                            getP2PEnrollmentUrl(
                                result.data.code,
                                'text',
                                inviteSource,
                                isAppsFlyerP2PEnabled
                            )
                        );
                        setEmailInviteUrl(
                            getP2PEnrollmentUrl(
                                result.data.code,
                                'email',
                                inviteSource,
                                isAppsFlyerP2PEnabled
                            )
                        );
                        setShareInviteUrl(
                            getP2PEnrollmentUrl(
                                result.data.code,
                                'share',
                                inviteSource,
                                isAppsFlyerP2PEnabled
                            )
                        );
                    }
                }
            }
        })();
    }, [selectedOrganizationId, isAppsFlyerP2PEnabled, inviteSource]);

    const onCopyCode = useCallback(() => {
        copyText(copyInviteUrl);
    }, [copyText, copyInviteUrl]);

    if (!isP2PInviteEnabled) {
        return <div />;
    }

    return (
        <div
            className={
                isInviteFriendsFamilyDrawingEnabled
                    ? styles.pageContainerDrawing
                    : styles.pageContainer
            }
        >
            <div
                className={
                    isInviteFriendsFamilyDrawingEnabled
                        ? styles.contentContainerDrawing
                        : styles.contentContainer
                }
            >
                <h1 className={styles.header}>Invite Friends & Family</h1>

                <div className="flex self-center">
                    {!IS_FLIPGIVE && (
                        <img
                            className="self-start pr-2"
                            style={{ marginTop: -5 }}
                            src={Stars}
                            alt=""
                            width="30"
                            height="43"
                        />
                    )}
                    <img
                        src={IS_FLIPGIVE ? FlipGiveIcon : LogoIconLightBg}
                        alt={IS_FLIPGIVE ? 'FlipGive Logo' : 'Raise Right Logo'}
                        width="95"
                        height="87"
                    />
                    {!IS_FLIPGIVE && (
                        <img
                            className="self-end pl-2"
                            style={{ transform: 'rotate(180deg)', marginBottom: -8 }}
                            src={Stars}
                            alt=""
                            width="30"
                            height="43"
                        />
                    )}
                </div>
                <div className="px-0 mt-8 flex flex-col items-center sm:px-0">
                    <h3 className={styles.subHeader}>
                        Invite friends and family to join {Branding} and help you earn
                    </h3>
                    <p className="tracking-normal text-grey-darkest text-base text-center pt-5 px-0 mb-4">
                        {isInviteFriendsFamilyDrawingEnabled ? (
                            <>
                                <strong>You could win a $500 gift card!</strong>
                                <br />
                                <div className="py-0.5">
                                    For every person who joins through your invite link, you’ll
                                    receive an entry into a monthly drawing for a $500 gift card of
                                    your choice.
                                </div>
                                <a
                                    className="no-underline"
                                    href={`${WEB_ENROLLMENT_ORIGIN}/m/referafriend`}
                                    target="_blank"
                                >
                                    See Details.
                                </a>
                            </>
                        ) : (
                            <></>
                        )}
                    </p>
                </div>
            </div>
            <div
                className={
                    isInviteFriendsFamilyDrawingEnabled
                        ? styles.contentContainerDrawing
                        : styles.contentContainer
                }
            >
                {copyInviteUrl ? (
                    <div
                        className={
                            isInviteFriendsFamilyDrawingEnabled ? 'px-2 sm:px-1' : 'px-0 sm:px-3'
                        }
                    >
                        <h5 className={styles.shareYourLink}>Share Link</h5>
                        <div className="mt-3 border border-grey-3 flex ju px-2 py-2.5 rounded">
                            <span className="w-11/12 sm:w-11/12 text-grey-3 overflow-ellipsis whitespace-nowrap overflow-hidden pr-2">
                                {copyInviteUrl}
                            </span>

                            <div className="w-1/12 sm:w-1/12 flex items-center justify-end">
                                <ResponsiveContainer For={ContainerType.desktop}>
                                    <button
                                        className={styles.copyButton}
                                        disabled={isCopied}
                                        onClick={() => {
                                            logP2PInviteFamily('copy', source);
                                            onCopyCode();
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            color={isCopied ? colors.success : colors.brand}
                                            style={{ fontSize: isCopied ? '18px' : '20px' }}
                                            icon={isCopied ? faCheck : faCopy}
                                            aria-label="Copy Invite"
                                        />
                                        <span className="ml-2">
                                            {isCopied ? 'Link Copied' : ''}
                                        </span>
                                    </button>
                                </ResponsiveContainer>

                                <ResponsiveContainer For={ContainerType.mobile}>
                                    <button
                                        className={styles.copyButton}
                                        disabled={isCopied}
                                        onClick={async () => {
                                            // try to share, if it fails, copy to clipboard
                                            try {
                                                await navigator.share(shareData);
                                            } catch (error) {
                                                onCopyCode();
                                            }

                                            logP2PInviteFamily('share', source);
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            className={styles.shareMobileIcon}
                                            color={colors.brand}
                                            style={{ fontSize: '20px' }}
                                            icon={faSignOut}
                                            aria-label="Share Invite"
                                        />
                                    </button>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                ) : (
                    <Skeleton height="80" width="200" />
                )}
            </div>
            <div className={styles.contentContainer}>
                <div className="flex self-center whitespace-nowrap">
                    <Btn
                        onClick={() => {
                            const emailFinalUrl = isAppsFlyerP2PEnabled
                                ? `${window.location.origin}${getP2PInviteRedirectPath(
                                      enrollmentCode,
                                      'email',
                                      inviteSource
                                  )}`
                                : emailInviteUrl;
                            const copy = getP2PCopyWeb(
                                orgName,
                                referrerName,
                                emailFinalUrl,
                                isP2PInviteCopyConfigWebEnabled
                            );
                            // Same encoding as develop / QuickActionsDetails: encodeURIComponent for mailto.
                            // Keep full mailto URL under ~2000 chars (limit in many clients) so body isn't truncated.
                            const MAILTO_MAX_URL = 2000;
                            const encodedSubject = encodeURIComponent(copy.emailSubject);
                            const prefix = `mailto:?subject=${encodedSubject}&body=`;
                            const maxBodyEncodedLength = MAILTO_MAX_URL - prefix.length;
                            let encodedBody = encodeURIComponent(copy.emailBody);
                            let bodyUsed = copy.emailBody;
                            if (encodedBody.length > maxBodyEncodedLength) {
                                const linkLine = `\n\nSign up using this referral link: ${emailFinalUrl}`;
                                let intro = copy.emailBody;
                                while (
                                    intro.length > 0 &&
                                    encodeURIComponent(intro + linkLine).length >
                                        maxBodyEncodedLength
                                ) {
                                    intro = intro.slice(0, -1);
                                }
                                bodyUsed = intro + linkLine;
                                encodedBody = encodeURIComponent(bodyUsed);
                            }
                            window.location.href = prefix + encodedBody;

                            logP2PInviteFamily('email', source);
                        }}
                        className={styles.btn}
                    >
                        <FontAwesomeIcon
                            color={colors.white}
                            style={{ fontSize: '18px', marginBottom: -1 }}
                            icon={faEnvelope}
                            aria-label="Send Email Invite"
                        />
                        <span className="pl-2">Email</span>
                    </Btn>
                    <ResponsiveContainer For={ContainerType.mobile}>
                        <Btn
                            onClick={() => {
                                const smsFinalUrl = isAppsFlyerP2PEnabled
                                    ? `${window.location.origin}${getP2PInviteRedirectPath(
                                          enrollmentCode,
                                          'text',
                                          inviteSource
                                      )}`
                                    : textInviteUrl;
                                const copy = getP2PCopyWeb(
                                    orgName,
                                    referrerName,
                                    smsFinalUrl,
                                    isP2PInviteCopyConfigWebEnabled
                                );
                                // Same encoding as develop / QuickActionsDetails: encodeURIComponent for sms body
                                const encodedSmsBody = encodeURIComponent(copy.smsBody);
                                const smsUrl = `sms:?&body=${encodedSmsBody}`;
                                window.open(smsUrl, '_self');

                                logP2PInviteFamily('text', source);
                            }}
                            className={styles.btn}
                            style={{ marginLeft: 5 }}
                        >
                            <FontAwesomeIcon
                                color={colors.white}
                                style={{ fontSize: '18px', marginBottom: -2 }}
                                icon={faCommentAltLines}
                                aria-label="Send Text Invite"
                            />
                            <span className="pl-2">Text</span>
                        </Btn>
                    </ResponsiveContainer>
                </div>
                <div className={styles.contentContainer}>
                    <div className="text-grey-medium text-xs text-center pt-2 px-4 mb-5">
                        <strong>Note:</strong> Please do not share this link on public websites or
                        social media pages, doing so may expose your organization to fraudulent
                        activity.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InviteFriendsFamily;
